import type { ReactNode } from 'react'
import { Icon } from '@/components/Icon'
import {
  TableHeader,
  TableCell,
  TableHost,
  ImageCell,
  DateCell,
} from '@/components/Table'
import { TableEmptyState } from '@/components/Table/TableEmptyState'
import {
  TableSearchbar,
  TableSearchbarMenuItem,
} from '@/components/TableSearchbar'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIllustration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { Checkbox } from '@/components/Checkbox'
import {
  Dropdown,
  DropdownTitle,
  DropdownItem,
  DropdownItemTitle,
} from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import { Button } from '@/components/Button'
import {
  ContextCell,
  AmountCell,
  IdentifierCell,
  FlagsCell,
} from '@/features/payouts/components/PayoutTransactionsTableCells'
import type { TransactionSourceType } from '@/features/payouts/transaction-source-type'
import type { UserTransactionReason } from '@/features/payouts/user-transaction-reason'
import type { TransactionFlag } from '@/features/payouts/transaction-flag'
import { FilterControlsToggleButton } from '@/components/FilterControlsToggleButton'
import type { Id } from '@/infra/id'
import { byDesign } from '@/infra/design'
import imgSearchNoresultsUrl from '@/assets/misc/img_search-noresults.svg'
import SearchNoResultsIllustration from '@/components/SpotIllustration/SearchNoResultsIllustration'
import imgJobCompletedUrl from '@/assets/misc/img_job-completed.svg'
import CompleteJobIllustration from '@/components/SpotIllustration/CompleteJobIllustration'

export interface PendingTransactionsTableProps {
  actionsDisabled: boolean
  searchText: string
  onSearchTextChange: (value: string) => unknown
  filtersShowing: boolean
  onToggleFilters: () => void
  appliedFilterCount: number
  isApprovePending: boolean
  selectedCount: number
  onApproveSelected: () => unknown
  approveMatchingFilterDisabled: boolean
  onApproveMatchingFilter: () => unknown
  isExportPending: boolean
  onExportCsv: () => unknown
  filterControls: ReactNode
  showTable: boolean
  appliedFilters: boolean
  onClearFilters: () => void
  children?: ReactNode
}

export function PendingTransactionsTable({
  actionsDisabled,
  searchText,
  onSearchTextChange,
  filtersShowing,
  onToggleFilters,
  appliedFilterCount,
  isApprovePending,
  selectedCount,
  onApproveSelected,
  approveMatchingFilterDisabled,
  onApproveMatchingFilter,
  isExportPending,
  onExportCsv,
  filterControls,
  showTable,
  appliedFilters,
  onClearFilters,
  children,
}: PendingTransactionsTableProps) {
  return (
    <>
      <TableHost>
        <TableSearchbar
          isDisabled={actionsDisabled}
          searchText={searchText}
          onSearchTextChange={onSearchTextChange}
        >
          <TableSearchbarMenuItem>
            <FilterControlsToggleButton
              active={filtersShowing}
              count={appliedFilterCount}
              onPress={onToggleFilters}
            />
          </TableSearchbarMenuItem>
          <TableSearchbarMenuItem>
            <Dropdown
              autoDismiss
              isDisabled={actionsDisabled || isApprovePending}
              button={
                <IconButton
                  name="more"
                  label="Transaction actions"
                  isPending={isApprovePending}
                  isDisabled={actionsDisabled}
                />
              }
            >
              <DropdownTitle>Actions</DropdownTitle>
              <DropdownItem
                icon={<Icon name="add" />}
                isDisabled={selectedCount === 0}
                onPress={onApproveSelected}
              >
                <DropdownItemTitle>
                  Add {selectedCount || ''} transactions to payout
                </DropdownItemTitle>
              </DropdownItem>
              <DropdownItem
                icon={<Icon name="add" />}
                isDisabled={approveMatchingFilterDisabled}
                onPress={onApproveMatchingFilter}
              >
                <DropdownItemTitle>
                  Add transactions matching filter to payout
                </DropdownItemTitle>
              </DropdownItem>
              <DropdownItem
                isDisabled={isExportPending}
                icon={<Icon name="download" />}
                onPress={onExportCsv}
              >
                <DropdownItemTitle>Download CSV</DropdownItemTitle>
              </DropdownItem>
            </Dropdown>
          </TableSearchbarMenuItem>
        </TableSearchbar>
        {filterControls}

        {showTable ? (
          children
        ) : (
          <PendingTransactionsEmptyState
            appliedFilters={appliedFilters}
            onClearFilters={onClearFilters}
          />
        )}
      </TableHost>
    </>
  )
}

export interface PendingTransactionsTableHeaderProps {
  actionsDisabled: boolean
  checked: boolean | 'indeterminate'
  onToggleSelection: (checked: boolean) => unknown
}

export function PendingTransactionsTableHeader({
  actionsDisabled,
  checked,
  onToggleSelection,
}: PendingTransactionsTableHeaderProps) {
  return (
    <>
      <TableHeader align="left">
        <Checkbox
          onChange={onToggleSelection}
          isDisabled={actionsDisabled}
          checked={checked}
        />
      </TableHeader>
      <TableHeader align="left">Context</TableHeader>
      <TableHeader align="left">Recipient</TableHeader>
      <TableHeader align="right">Amount</TableHeader>
      <TableHeader align="left">Workspace</TableHeader>
      <TableHeader align="left">ID</TableHeader>
      <TableHeader align="left">Created</TableHeader>
      <TableHeader align="left">Transacted</TableHeader>
      <TableHeader align="left">Iteration</TableHeader>
      <TableHeader align="left">Flags</TableHeader>
      <TableHeader align="right"></TableHeader>
    </>
  )
}

export interface PendingTransactionRowProps {
  actionsDisabled: boolean
  selected: boolean
  onToggleSelection: (checked: boolean) => unknown
  isPending: boolean
  id: Id
  source: TransactionSourceType
  memo: string | null
  reason: UserTransactionReason | null
  jobId: string | null
  lastFailureDescription: string | null
  recipientName: string
  recipientAvatar: string | null
  recipientAvatarFallback: string | null
  amount: number
  workspaceName: string
  workspaceIcon: string
  shortId: string
  createdAt: Date
  transactedAt: Date
  iteration: number
  flags: TransactionFlag[]
  onApprove: () => unknown
  onRequestCancellation: () => unknown
  onViewJobInHQ: () => unknown
  onViewJob: () => unknown
}

export function PendingTransactionRow({
  actionsDisabled,
  selected,
  onToggleSelection,
  isPending,
  id,
  source,
  memo,
  reason,
  jobId,
  lastFailureDescription,
  recipientName,
  recipientAvatar,
  recipientAvatarFallback,
  amount,
  workspaceName,
  workspaceIcon,
  shortId,
  createdAt,
  transactedAt,
  iteration,
  flags,
  onApprove,
  onRequestCancellation,
  onViewJobInHQ,
  onViewJob,
}: PendingTransactionRowProps) {
  return (
    <>
      <TableCell width={64}>
        <Checkbox
          isDisabled={actionsDisabled}
          checked={selected}
          onChange={onToggleSelection}
        />
      </TableCell>
      <ContextCell
        source={source}
        userTransactionMemo={memo}
        userTransactionReason={reason}
        jobTransactionJobId={jobId}
        lastFailureDescription={lastFailureDescription}
      />
      <ImageCell
        title={recipientName}
        image={recipientAvatar}
        imageFallback={recipientAvatarFallback}
        minWidth={110}
      />
      <AmountCell amount={amount} />
      <ImageCell title={workspaceName} image={workspaceIcon} minWidth={110} />
      <IdentifierCell value={shortId} />
      <DateCell
        date={createdAt}
        testId={`pending-transaction-date-created-${id}`}
      />
      <DateCell
        date={transactedAt}
        testId={`pending-transaction-date-transacted-${id}`}
      />
      <TableCell>{iteration}</TableCell>
      <FlagsCell flags={flags} />
      <TableCell align="right">
        <Dropdown
          autoDismiss
          isDisabled={actionsDisabled}
          button={
            // Needs to be inline, otherwise it doesn't align
            // correctly with the `more` button in the searchbar.
            <IconButton
              name="more"
              label="Transaction actions"
              inline
              isPending={isPending}
              isDisabled={isPending || actionsDisabled}
            />
          }
        >
          <DropdownTitle>Actions</DropdownTitle>
          <DropdownItem onPress={onApprove} icon={<Icon name="add" />}>
            Add to payout
          </DropdownItem>
          {source === 'User' && (
            <DropdownItem
              onPress={onRequestCancellation}
              icon={<Icon name="close" />}
            >
              Cancel
            </DropdownItem>
          )}
          {jobId && (
            <>
              <DropdownItem onPress={onViewJobInHQ} icon={<Icon name="view" />}>
                View job in HQ
              </DropdownItem>
              <DropdownItem onPress={onViewJob} icon={<Icon name="view" />}>
                View job in Work Portal
              </DropdownItem>
            </>
          )}
        </Dropdown>
      </TableCell>
    </>
  )
}

interface PendingTransactionsEmptyStateProps {
  appliedFilters: boolean
  onClearFilters: () => void
}

function PendingTransactionsEmptyState({
  appliedFilters,
  onClearFilters,
}: PendingTransactionsEmptyStateProps) {
  return (
    <TableEmptyState bordered>
      {appliedFilters ? (
        <EmptyState size="sm">
          <EmptyStateIllustration
            src={imgSearchNoresultsUrl}
            illustration={byDesign(undefined, SearchNoResultsIllustration)}
          />
          <EmptyStateTitle>
            We couldn't find what you were looking for.
          </EmptyStateTitle>
          <EmptyStateDescription>
            Maybe try{' '}
            <Button
              label="clearing your search filter"
              size="baseline"
              variant="text"
              onPress={onClearFilters}
            />
            ?
          </EmptyStateDescription>
        </EmptyState>
      ) : (
        <EmptyState size="sm">
          <EmptyStateIllustration
            src={imgJobCompletedUrl}
            illustration={byDesign(undefined, CompleteJobIllustration)}
          />
          <EmptyStateTitle>No transactions left to approve.</EmptyStateTitle>
          <EmptyStateDescription>
            Pending transactions will appear here.
          </EmptyStateDescription>
        </EmptyState>
      )}
    </TableEmptyState>
  )
}
